import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, TableShell, EmptyState, Modal, Field, Input, Textarea, SelectBox, Button, StatusBadge,
} from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import InvoiceDocument from "@/components/InvoiceDocument";
import { Plus, Receipt, Pencil, Trash2, Eye, Printer, Download, CreditCard, Trash } from "lucide-react";

const EMPTY = {
  client_id: "", customer_name: "", customer_email: "", customer_phone: "",
  address: "", city: "", state: "", zip: "",
  line_items: [{ service: "", description: "", qty: 1, price: 0 }],
  tax_rate: 0, discount: 0, deposit: 0,
  issue_date: new Date().toISOString().slice(0, 10),
  due_date: "", notes: "",
};

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({ tax_rate: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({ amount: 0, method: "Cash", date: new Date().toISOString().slice(0, 10), notes: "" });
  const [payInvoice, setPayInvoice] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const { t } = useI18n();

  const load = () => api.get("/invoices").then(({ data }) => setInvoices(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => {
    setForm({ ...EMPTY, tax_rate: settings.tax_rate || 0, notes: settings.invoice_terms || "", line_items: [{ service: "", description: "", qty: 1, price: 0 }] });
    setEditing(null); setOpen(true);
  };
  const openEdit = (inv) => { setForm({ ...EMPTY, ...inv }); setEditing(inv.id); setOpen(true); };

  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, client_id: cid, customer_name: c.name, customer_email: c.email, customer_phone: c.phone, address: c.address, city: c.city, state: c.state, zip: c.zip }));
    else set("client_id", "");
  };
  const setItem = (i, k, v) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [k]: v };
    if (k === "service") { const s = services.find((x) => x.name === v); if (s && !items[i].price) items[i].price = s.base_price; }
    set("line_items", items);
  };
  const addItem = () => set("line_items", [...form.line_items, { service: "", description: "", qty: 1, price: 0 }]);
  const removeItem = (i) => set("line_items", form.line_items.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.customer_name) return toast.error(t("Customer name is required"));
    const payload = {
      ...form, tax_rate: Number(form.tax_rate), discount: Number(form.discount), deposit: Number(form.deposit),
      line_items: form.line_items.map((li) => ({ ...li, qty: Number(li.qty), price: Number(li.price) })),
    };
    try {
      if (editing) await api.put(`/invoices/${editing}`, payload);
      else await api.post("/invoices", payload);
      toast.success(editing ? t("Invoice updated") : t("Invoice created"));
      setOpen(false); load();
    } catch { toast.error("Could not save invoice"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this invoice and its payments?")) return;
    await api.delete(`/invoices/${id}`); toast.success(t("Invoice deleted")); load();
  };

  const openPay = (inv) => {
    setPayInvoice(inv);
    setPay({ amount: inv.balance_due, method: "Cash", date: new Date().toISOString().slice(0, 10), notes: "" });
    setPayOpen(true);
  };
  const savePayment = async () => {
    if (!pay.amount || Number(pay.amount) <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post("/payments", { invoice_id: payInvoice.id, amount: Number(pay.amount), method: pay.method, date: pay.date, notes: pay.notes });
      toast.success(t("Payment recorded"));
      setPayOpen(false); load();
      if (preview) api.get(`/invoices/${preview.id}`).then(({ data }) => setPreview(data));
    } catch { toast.error("Could not record payment"); }
  };

  const doPrint = () => window.print();
  const doPdf = async () => {
    const el = document.getElementById("invoice-print-area");
    if (!el) return;
    setPdfBusy(true);
    try {
      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "letter" });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // Scale to fit one Letter page while preserving aspect ratio.
      const ratio = Math.min(pageW / canvas.width, pageH / canvas.height);
      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;
      const x = (pageW - imgW) / 2;
      pdf.addImage(imgData, "PNG", x, 0, imgW, imgH);
      const num = preview?.invoice_number || "invoice";
      pdf.save(`Invoice-${num}.pdf`);
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Invoices" subtitle="One invoice layout for preview, print, and PDF. Auto-numbered MT-####."
        action={<PrimaryButton onClick={openNew} data-testid="add-invoice-button"><Plus size={16} /> {t("New Invoice")}</PrimaryButton>} />

      {invoices.length === 0 ? (
        <EmptyState icon={Receipt} title="No invoices yet" subtitle="Complete a scheduled job to auto-generate an invoice, or create one manually."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("New Invoice")}</PrimaryButton>} />
      ) : (
        <TableShell headers={["Invoice #", "Customer", "Total", "Balance Due", "Status", "Actions"]} testid="invoices-table">
          {invoices.map((inv) => (
            <tr key={inv.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4 font-medium text-[#5A189A]">{inv.invoice_number}</td>
              <td className="py-3.5 px-4 text-sm">{inv.customer_name}</td>
              <td className="py-3.5 px-4 font-medium">{money(inv.total)}</td>
              <td className="py-3.5 px-4">{inv.credit_balance > 0 ? <span className="text-green-600">+{money(inv.credit_balance)} {t("credit")}</span> : money(inv.balance_due)}</td>
              <td className="py-3.5 px-4"><StatusBadge status={inv.status} /></td>
              <td className="py-3.5 px-4">
                <div className="flex items-center gap-1">
                  <IconBtn onClick={() => setPreview(inv)} title="Preview" testid={`view-invoice-${inv.id}`}><Eye size={16} /></IconBtn>
                  {inv.balance_due > 0 && <IconBtn onClick={() => openPay(inv)} title="Record Payment" testid={`pay-invoice-${inv.id}`}><CreditCard size={16} /></IconBtn>}
                  <IconBtn onClick={() => openEdit(inv)} title="Edit"><Pencil size={16} /></IconBtn>
                  <IconBtn onClick={() => remove(inv.id)} title="Delete" danger><Trash2 size={16} /></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      {/* Editor */}
      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Invoice" : "New Invoice"} size="xl"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-invoice-button">{t("Save Invoice")}</PrimaryButton></>}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Client"><SelectBox value={form.client_id} onChange={(e) => pickClient(e.target.value)} options={clients.map((c) => ({ value: c.id, label: c.name }))} placeholder="Select client" /></Field>
            <Field label="Customer Name *"><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="invoice-customer-input" /></Field>
            <Field label="Email"><Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></Field>
            <Field label="Address" className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label="Issue Date"><Input type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} /></Field>
            <Field label="Due Date"><Input type="date" value={form.due_date} onChange={(e) => set("due_date", e.target.value)} /></Field>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-[#121212]">Line Items</label>
              <button onClick={addItem} className="text-[#5A189A] text-sm font-medium flex items-center gap-1 hover:text-[#D4AF37]"><Plus size={14} /> Add Item</button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4"><SelectBox value={li.service} onChange={(e) => setItem(i, "service", e.target.value)} options={services.map((s) => s.name)} placeholder="Service" /></div>
                  <div className="col-span-3"><Input placeholder="Description" value={li.description} onChange={(e) => setItem(i, "description", e.target.value)} /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={li.qty} onChange={(e) => setItem(i, "qty", e.target.value)} /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Price" value={li.price} onChange={(e) => setItem(i, "price", e.target.value)} /></div>
                  <div className="col-span-1 flex justify-center">{form.line_items.length > 1 && <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><Trash size={16} /></button>}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Tax Rate (%)"><Input type="number" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} /></Field>
            <Field label="Discount ($)"><Input type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} /></Field>
            <Field label="Deposit ($)"><Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} /></Field>
          </div>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
      </Modal>

      {/* Payment modal */}
      <Modal open={payOpen} onClose={() => setPayOpen(false)} title={`${t("Record Payment")} · ${payInvoice?.invoice_number || ""}`} size="md"
        footer={<><Button variant="outline" onClick={() => setPayOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={savePayment} data-testid="save-payment-button">{t("Record Payment")}</PrimaryButton></>}>
        <div className="space-y-4">
          <div className="bg-[#faf8fd] rounded-xl p-3 text-sm flex justify-between"><span className="text-[#52525B]">{t("Balance Due")}</span><span className="font-bold text-[#5A189A]">{money(payInvoice?.balance_due)}</span></div>
          <Field label="Amount ($)"><Input type="number" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
          <Field label="Method"><SelectBox value={pay.method} onChange={(e) => setPay({ ...pay, method: e.target.value })} options={["Cash", "Card", "Check", "Zelle", "Venmo", "Bank Transfer"]} /></Field>
          <Field label="Date"><Input type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} /></Field>
          <Field label="Notes"><Input value={pay.notes} onChange={(e) => setPay({ ...pay, notes: e.target.value })} /></Field>
        </div>
      </Modal>

      {/* Preview / Print / PDF */}
      <Modal open={!!preview} onClose={() => setPreview(null)} title="Invoice Preview" size="xl"
        footer={
          <div className="flex gap-2 no-print">
            {preview?.balance_due > 0 && <Button variant="outline" onClick={() => openPay(preview)}><CreditCard size={15} className="mr-1" /> {t("Payment")}</Button>}
            <Button variant="outline" onClick={doPdf} disabled={pdfBusy} data-testid="invoice-pdf-button"><Download size={15} className="mr-1" /> {pdfBusy ? t("Generating...") : "PDF"}</Button>
            <PrimaryButton onClick={doPrint} data-testid="invoice-print-button"><Printer size={15} /> {t("Print")}</PrimaryButton>
          </div>
        }>
        <div id="invoice-preview-frame" className="bg-white -mx-6 px-2 py-4 rounded-xl overflow-x-auto">
          <div className="mt-card mx-auto" style={{ width: "fit-content" }}>
            <InvoiceDocument invoice={preview} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
